import { Camera, Tv, Volume2, Users, MonitorSmartphone, Coffee, Wifi, HelpCircle } from "lucide-react";
import { ThemeIcon, Group, Text, type MantineRadius } from "@mantine/core";

type AmenityConfig = { icon: typeof Camera; color: string; label: string };

/** Canonical seat entry. ``seat`` (singular) is the key the domain types use
 *  — ``MeetingRoomAmenity.type`` in ``@nodus/booking`` and ``computeAmenities``
 *  in ``@nodus/floorplan`` both emit it — and ``seats`` is kept as an alias so
 *  a caller guessing the plural still gets the right icon instead of the
 *  unknown-key "?" fallback. */
const SEAT_CONFIG: AmenityConfig = { icon: Users, color: "blue", label: "Seats" };

const AMENITY_CONFIG: Record<string, AmenityConfig> = {
  camera: { icon: Camera, color: "violet", label: "Camera" },
  tv: { icon: Tv, color: "cyan", label: "Display" },
  speaker: { icon: Volume2, color: "orange", label: "Speaker" },
  seat: SEAT_CONFIG,
  seats: SEAT_CONFIG,
  whiteboard: { icon: MonitorSmartphone, color: "teal", label: "Whiteboard" },
  coffee: { icon: Coffee, color: "yellow", label: "Coffee" },
  wifi: { icon: Wifi, color: "green", label: "WiFi" },
};

export interface AmenityIconProps {
  /** Amenity key — known keys get specific icon+color, unknown keys get "?" icon */
  type: string;
  /** Count (e.g. 4 seats) — shown in label */
  count?: number;
  /** Custom icon component — overrides the built-in icon for this type */
  icon?: React.ComponentType<{ size?: number }>;
  /** Custom color — overrides the built-in color */
  color?: string;
  /** Custom label — overrides the built-in label */
  label?: string;
  /** Use colored ThemeIcon background */
  useColor?: boolean;
  /** Show label text next to icon */
  showLabel?: boolean;
  /** Icon size in px */
  size?: number;
  /** Radius of the coloured ThemeIcon tile (ignored when ``useColor`` is false) */
  radius?: MantineRadius;
  /** Label font size. Defaults to ``"xs"``.
   *
   *  Exists for the wall-mounted kiosk: ``FindFreeRoomModal`` in touch mode
   *  enlarges every control so a panel is readable from across a room, and a
   *  seat count pinned at 12px defeats that. Callers that are not a kiosk
   *  should leave it alone. */
  labelSize?: string | number;
}

export function AmenityIcon({
  type,
  count,
  icon: customIcon,
  color: customColor,
  label: customLabel,
  useColor = true,
  showLabel = true,
  size = 16,
  radius = "sm",
  labelSize = "xs",
}: AmenityIconProps) {
  const config = AMENITY_CONFIG[type];
  const Icon = customIcon ?? config?.icon ?? HelpCircle;
  const color = customColor ?? config?.color ?? "gray";
  const defaultLabel = customLabel ?? config?.label ?? type;
  const displayLabel = count != null ? `${count} ${defaultLabel.toLowerCase()}` : defaultLabel;

  if (!useColor) {
    return (
      <Group gap={4} wrap="nowrap">
        <Icon size={size} style={{ color: `var(--mantine-color-${color}-5)`, flexShrink: 0 }} />
        {showLabel && <Text fz={labelSize} c="dimmed">{displayLabel}</Text>}
      </Group>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <ThemeIcon variant="light" color={color} size={size + 8} radius={radius}>
        <Icon size={size} />
      </ThemeIcon>
      {showLabel && <Text fz={labelSize}>{displayLabel}</Text>}
    </Group>
  );
}

export interface AmenityItem {
  type: string;
  count?: number;
  icon?: React.ComponentType<{ size?: number }>;
  color?: string;
  label?: string;
}

/** Render a row of amenity icons */
export function AmenityRow({
  items,
  useColor = true,
  showLabel = true,
  size = 14,
  radius = "sm",
  labelSize = "xs",
}: {
  items: AmenityItem[];
  useColor?: boolean;
  showLabel?: boolean;
  size?: number;
  radius?: MantineRadius;
  labelSize?: string | number;
}) {
  if (items.length === 0) return null;

  return (
    <Group gap="xs" wrap="wrap">
      {/* Keyed by position, not ``type``: two items of the same type (two
          ``seat`` entries, one broken and one not) are legitimate and must
          both render. The list is derived fresh from props on every render
          and is never reordered in place, so the index is stable enough. */}
      {items.map((item, index) => (
        <AmenityIcon key={`${item.type}-${index}`} type={item.type} count={item.count} icon={item.icon} color={item.color} label={item.label} useColor={useColor} showLabel={showLabel} size={size} radius={radius} labelSize={labelSize} />
      ))}
    </Group>
  );
}
